import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, Flag, Settings, LogOut, Plus, Trash2, Edit, RefreshCw, FileSpreadsheet, Download, Wifi, WifiOff, ChevronUp, ChevronDown, Upload, Trophy, Clock, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import NotificationBell from '../components/NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [stages, setStages] = useState([]);
  const [timingRecords, setTimingRecords] = useState([]);
  const [liveTimingEnabled, setLiveTimingEnabled] = useState(true);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRecordEvent, setSelectedRecordEvent] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', name: '', roles: ['marshal'], email: '', phone: '' });
  const [newEvent, setNewEvent] = useState({ name: '', description: '', start_date: '', end_date: '' });
  const [newStage, setNewStage] = useState({ event_id: '', name: '', stage_type: 'stage', stage_number: 1, distance_km: 0 });
  const [loading, setLoading] = useState(false);
  
  // Entry List & Results state
  const [entryList, setEntryList] = useState([]);
  const [selectedResultsEvent, setSelectedResultsEvent] = useState(null);
  const [liveResults, setLiveResults] = useState({ results: [], stages: [], classes: [] });
  const [penalties, setPenalties] = useState([]);
  const [stageTimeEdits, setStageTimeEdits] = useState([]);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showPenaltyDialog, setShowPenaltyDialog] = useState(false);
  const [newPenalty, setNewPenalty] = useState({ car_number: '', penalty_time: '', reason: '' });
  const [showEditTimeDialog, setShowEditTimeDialog] = useState(false);
  const [editingStageTime, setEditingStageTime] = useState(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState('');
  const fileInputRef = useRef(null);
  const [appSettings, setAppSettings] = useState({ 
    timezone: 'UTC', 
    sync_interval_hours: 1,
    live_results_enabled: false,
    public_results_enabled: false,
    results_finalized: false,
    results_finalized_at: null
  });
  const [selectedPhotoRecord, setSelectedPhotoRecord] = useState(null);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showEditEventDialog, setShowEditEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditStageDialog, setShowEditStageDialog] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [allStages, setAllStages] = useState([]); // For stage lookup in records
  const [selectedRecords, setSelectedRecords] = useState([]); // Multi-select for records deletion
  const [selectedEvents, setSelectedEvents] = useState([]); // Multi-select for events deletion
  const [selectedStages, setSelectedStages] = useState([]); // Multi-select for stages deletion

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchEvents();
    fetchTimingRecords();
    fetchLiveTimingSettings();
    fetchAppSettings();
    fetchAllStages();
    
    // Auto-refresh data every 10 seconds (only timing records, not events - to avoid state changes)
    const refreshInterval = setInterval(() => {
      fetchTimingRecords(null, false); // Silent auto-refresh
    }, 10000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Fetch all stages for lookup
  const fetchAllStages = async () => {
    try {
      const response = await axios.get(`${API}/stages`, getAuthHeaders());
      setAllStages(response.data);
    } catch (error) {
      console.error('Failed to fetch all stages');
    }
  };

  // Helper to get stage display info
  const getStageDisplay = (stageId) => {
    const stage = allStages.find(s => s.id === stageId);
    if (!stage) return { label: '-', name: 'Unknown' };
    if (stage.stage_type === 'stage') return { label: `SS${stage.stage_number}`, name: stage.name };
    if (stage.stage_type === 'service_park') return { label: 'Service', name: stage.name };
    if (stage.stage_type === 'rally_start') return { label: 'Section', name: stage.name };
    return { label: '-', name: stage.name };
  };

  useEffect(() => {
    if (selectedEvent) {
      fetchStages(selectedEvent.id);
    }
  }, [selectedEvent]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, getAuthHeaders());
      setEvents(response.data);
      if (response.data.length > 0 && !selectedEvent) {
        setSelectedEvent(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch events');
    }
  };

  const fetchStages = async (eventId) => {
    try {
      const response = await axios.get(`${API}/stages?event_id=${eventId}`, getAuthHeaders());
      setStages(response.data);
    } catch (error) {
      toast.error('Failed to fetch stages');
    }
  };

  const fetchTimingRecords = async (eventId = null, showErrors = true) => {
    setLoading(true);
    try {
      const targetEventId = eventId || selectedRecordEvent;
      // Include photos to display them properly
      let url = `${API}/timing-records?limit=100&include_photos=true`;
      if (targetEventId) {
        url += `&event_id=${targetEventId}`;
      }
      const response = await axios.get(url, getAuthHeaders());
      setTimingRecords(response.data);
      // Count photos
      const count = response.data.filter(r => r.photo_data && r.photo_data.startsWith('data:image')).length;
      setPhotoCount(count);
    } catch (error) {
      // Only show errors on manual refresh, not auto-refresh
      if (showErrors) {
        toast.error('Failed to fetch timing records');
      }
      console.error('Fetch timing records error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveTimingSettings = async () => {
    try {
      const response = await axios.get(`${API}/live-timing/settings`);
      setLiveTimingEnabled(response.data.enabled);
    } catch (error) {
      console.error('Failed to fetch live timing settings');
    }
  };

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/app-settings`);
      setAppSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch app settings');
    }
  };

  const createUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.name) {
      toast.error('Please fill username, password and name');
      return;
    }
    
    if (!newUser.roles || newUser.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, newUser, getAuthHeaders());
      toast.success('User created successfully');
      setShowUserDialog(false);
      setNewUser({ username: '', password: '', name: '', roles: ['marshal'], email: '', phone: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async () => {
    if (!editingUser?.name || !editingUser?.username) {
      toast.error('Please fill username and name');
      return;
    }
    
    // Ensure roles is an array with at least one item
    const userRoles = editingUser.roles || [editingUser.role || 'marshal'];
    if (userRoles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        username: editingUser.username,
        name: editingUser.name,
        email: editingUser.email || null,
        phone: editingUser.phone || null,
        roles: userRoles,
        is_active: editingUser.is_active
      }, getAuthHeaders());
      toast.success('User updated successfully');
      setShowEditUserDialog(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeaders());
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !currentStatus }, getAuthHeaders());
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const createEvent = async () => {
    if (!newEvent.name) {
      toast.error('Please enter event name');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/events`, newEvent, getAuthHeaders());
      toast.success('Event created successfully');
      setShowEventDialog(false);
      setNewEvent({ name: '', description: '', start_date: '', end_date: '' });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  const createStage = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }
    
    if (!newStage.name) {
      toast.error('Please enter stage/service park name');
      return;
    }

    // Validate stage number only for stage type
    if (newStage.stage_type === 'stage' && (newStage.stage_number === '' || newStage.stage_number < 1)) {
      toast.error('Please enter a valid stage number');
      return;
    }

    if (newStage.distance_km === '' || parseFloat(newStage.distance_km) < 0) {
      toast.error('Please enter a valid distance');
      return;
    }

    setLoading(true);
    try {
      // Always use the selected event's ID and convert values
      const stageData = {
        event_id: selectedEvent.id,
        name: newStage.name,
        stage_type: newStage.stage_type,
        stage_number: newStage.stage_type === 'stage' ? parseInt(newStage.stage_number) : null,
        distance_km: parseFloat(newStage.distance_km)
      };
      await axios.post(`${API}/stages`, stageData, getAuthHeaders());
      toast.success(`${newStage.stage_type === 'stage' ? 'Stage' : newStage.stage_type === 'service_park' ? 'Service Park' : 'Rally Section'} added to ${selectedEvent.name}`);
      setShowStageDialog(false);
      setNewStage({ event_id: selectedEvent.id, name: '', stage_type: 'stage', stage_number: 1, distance_km: 0 });
      fetchStages(selectedEvent.id);
    } catch (error) {
      toast.error('Failed to create stage');
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This will also delete all associated stages and timing records.')) return;

    try {
      await axios.delete(`${API}/events/${eventId}`, getAuthHeaders());
      toast.success('Event deleted successfully');
      fetchEvents();
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(null);
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const setCurrentEvent = async (eventId) => {
    try {
      await axios.put(`${API}/events/${eventId}/set-current`, {}, getAuthHeaders());
      toast.success('Event set as current');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to set current event');
    }
  };

  const deleteStage = async (stageId) => {
    if (!window.confirm('Are you sure you want to delete this stage?')) return;

    try {
      await axios.delete(`${API}/stages/${stageId}`, getAuthHeaders());
      toast.success('Stage deleted successfully');
      setSelectedStages(prev => prev.filter(id => id !== stageId));
      if (selectedEvent) fetchStages(selectedEvent.id);
    } catch (error) {
      toast.error('Failed to delete stage');
    }
  };

  const deleteSelectedEvents = async () => {
    if (selectedEvents.length === 0) {
      toast.error('No events selected');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedEvents.length} event(s)? This will also delete all associated stages and timing records.`)) return;

    try {
      let deleted = 0;
      let failed = 0;
      for (const eventId of selectedEvents) {
        try {
          await axios.delete(`${API}/events/${eventId}`, getAuthHeaders());
          deleted++;
        } catch (e) {
          failed++;
        }
      }
      if (deleted > 0) toast.success(`${deleted} event(s) deleted successfully`);
      if (failed > 0) toast.error(`${failed} event(s) failed to delete`);
      setSelectedEvents([]);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete events');
    }
  };

  const deleteSelectedStages = async () => {
    if (selectedStages.length === 0) {
      toast.error('No stages selected');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedStages.length} stage(s)?`)) return;

    try {
      let deleted = 0;
      let failed = 0;
      for (const stageId of selectedStages) {
        try {
          await axios.delete(`${API}/stages/${stageId}`, getAuthHeaders());
          deleted++;
        } catch (e) {
          failed++;
        }
      }
      if (deleted > 0) toast.success(`${deleted} stage(s) deleted successfully`);
      if (failed > 0) toast.error(`${failed} stage(s) failed to delete`);
      setSelectedStages([]);
      if (selectedEvent) fetchStages(selectedEvent.id);
    } catch (error) {
      toast.error('Failed to delete stages');
    }
  };

  const toggleEventSelection = (eventId) => {
    setSelectedEvents(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId) 
        : [...prev, eventId]
    );
  };

  const toggleStageSelection = (stageId) => {
    setSelectedStages(prev => 
      prev.includes(stageId) 
        ? prev.filter(id => id !== stageId) 
        : [...prev, stageId]
    );
  };

  const selectAllEvents = () => {
    if (selectedEvents.length === events.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(events.map(e => e.id));
    }
  };

  const selectAllStages = () => {
    if (selectedStages.length === stages.length) {
      setSelectedStages([]);
    } else {
      setSelectedStages(stages.map(s => s.id));
    }
  };

  // Reorder events - move item up or down
  const reorderEvents = async (eventId, direction) => {
    const currentIndex = events.findIndex(e => e.id === eventId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= events.length) return;
    
    // Create new order array
    const newOrder = [...events];
    const [moved] = newOrder.splice(currentIndex, 1);
    newOrder.splice(newIndex, 0, moved);
    
    // Get event IDs in new order
    const eventIds = newOrder.map(e => e.id);
    
    try {
      await axios.put(`${API}/events/reorder`, { event_ids: eventIds }, getAuthHeaders());
      setEvents(newOrder);
      toast.success('Events reordered');
    } catch (error) {
      toast.error('Failed to reorder events');
    }
  };

  // Reorder stages - move item up or down
  const reorderStages = async (stageId, direction) => {
    const currentIndex = stages.findIndex(s => s.id === stageId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= stages.length) return;
    
    // Create new order array
    const newOrder = [...stages];
    const [moved] = newOrder.splice(currentIndex, 1);
    newOrder.splice(newIndex, 0, moved);
    
    // Get stage IDs in new order
    const stageIds = newOrder.map(s => s.id);
    
    try {
      await axios.put(`${API}/stages/reorder`, { stage_ids: stageIds }, getAuthHeaders());
      setStages(newOrder);
      toast.success('Stages reordered');
    } catch (error) {
      toast.error('Failed to reorder stages');
    }
  };

  const toggleLiveTiming = async () => {
    try {
      await axios.put(`${API}/live-timing/settings?enabled=${!liveTimingEnabled}`, {}, getAuthHeaders());
      setLiveTimingEnabled(!liveTimingEnabled);
      toast.success(`Live timing ${!liveTimingEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update live timing settings');
    }
  };

  const exportToExcel = async () => {
    try {
      const url = selectedRecordEvent 
        ? `${API}/timing-records/export/excel?event_id=${selectedRecordEvent.id}` 
        : `${API}/timing-records/export/excel`;
      
      const response = await axios.get(url, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `vosloor_timing_records_${Date.now()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export Excel file');
    }
  };

  const exportPhotos = async () => {
    try {
      const url = selectedRecordEvent 
        ? `${API}/timing-records/export/photos?event_id=${selectedRecordEvent.id}` 
        : `${API}/timing-records/export/photos`;
      
      const response = await axios.get(url, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `vosloor_timing_photos_${Date.now()}.zip`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Photos ZIP file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export photos');
    }
  };

  const groupRecordsByCar = () => {
    const grouped = {};
    timingRecords.forEach(record => {
      if (!grouped[record.car_number]) {
        grouped[record.car_number] = [];
      }
      grouped[record.car_number].push(record);
    });
    return grouped;
  };

  const updateAppSettings = async (settingsToUpdate = null) => {
    const settings = settingsToUpdate || appSettings;
    try {
      await axios.put(
        `${API}/app-settings`,
        {
          timezone: settings.timezone,
          sync_interval_hours: settings.sync_interval_hours,
          live_results_enabled: settings.live_results_enabled,
          public_results_enabled: settings.public_results_enabled,
          results_finalized: settings.results_finalized
        },
        getAuthHeaders()
      );
      toast.success('Settings updated');
      // Refresh settings to get updated finalized_at timestamp
      fetchAppSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const deleteTimingRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this timing record? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/timing-records/${recordId}`, getAuthHeaders());
      toast.success('Timing record deleted successfully');
      setSelectedRecords(prev => prev.filter(id => id !== recordId));
      fetchTimingRecords();
    } catch (error) {
      toast.error('Failed to delete timing record');
    }
  };

  const deleteSelectedRecords = async () => {
    if (selectedRecords.length === 0) {
      toast.error('No records selected');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedRecords.length} timing record(s)? This action cannot be undone.`)) return;

    try {
      let deleted = 0;
      let failed = 0;
      for (const recordId of selectedRecords) {
        try {
          await axios.delete(`${API}/timing-records/${recordId}`, getAuthHeaders());
          deleted++;
        } catch (e) {
          failed++;
        }
      }
      if (deleted > 0) toast.success(`${deleted} record(s) deleted successfully`);
      if (failed > 0) toast.error(`${failed} record(s) failed to delete`);
      setSelectedRecords([]);
      fetchTimingRecords();
    } catch (error) {
      toast.error('Failed to delete records');
    }
  };

  const toggleRecordSelection = (recordId) => {
    setSelectedRecords(prev => 
      prev.includes(recordId) 
        ? prev.filter(id => id !== recordId) 
        : [...prev, recordId]
    );
  };

  const selectAllRecordsForCar = (carRecords) => {
    const allIds = carRecords.map(r => r.id);
    const allSelected = allIds.every(id => selectedRecords.includes(id));
    if (allSelected) {
      setSelectedRecords(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedRecords(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  // ==================== ENTRY LIST FUNCTIONS ====================
  
  const fetchEntryList = async (eventId) => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/entry-list?event_id=${eventId}`, getAuthHeaders());
      setEntryList(response.data);
    } catch (error) {
      toast.error('Failed to fetch entry list');
    }
  };

  const handleEntryListUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedResultsEvent) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/entry-list/upload?event_id=${selectedResultsEvent.id}`,
        formData,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Uploaded: ${response.data.entries_created} created, ${response.data.entries_updated} updated`);
      fetchEntryList(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload entry list');
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const createOrUpdateEntry = async () => {
    if (!editingEntry || !selectedResultsEvent) return;
    
    setLoading(true);
    try {
      if (editingEntry.id) {
        await axios.put(`${API}/entry-list/${editingEntry.id}`, editingEntry, getAuthHeaders());
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/entry-list`, { ...editingEntry, event_id: selectedResultsEvent.id }, getAuthHeaders());
        toast.success('Entry created');
      }
      setShowEntryDialog(false);
      setEditingEntry(null);
      fetchEntryList(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id);
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/entry-list/${entryId}`, getAuthHeaders());
      toast.success('Entry deleted');
      fetchEntryList(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id);
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const updateEntryStatus = async (entryId, newStatus) => {
    try {
      await axios.put(`${API}/entry-list/${entryId}`, { entry_status: newStatus }, getAuthHeaders());
      toast.success(`Status updated to ${newStatus.toUpperCase()}`);
      fetchEntryList(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // ==================== PENALTIES FUNCTIONS ====================
  
  const fetchPenalties = async (eventId) => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/penalties?event_id=${eventId}`, getAuthHeaders());
      setPenalties(response.data);
    } catch (error) {
      console.error('Failed to fetch penalties');
    }
  };

  const addPenalty = async () => {
    if (!newPenalty.car_number || !newPenalty.penalty_time || !selectedResultsEvent) {
      toast.error('Car number and penalty time required');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/penalties`, {
        event_id: selectedResultsEvent.id,
        car_number: newPenalty.car_number,
        penalty_time: newPenalty.penalty_time,
        reason: newPenalty.reason
      }, getAuthHeaders());
      toast.success('Penalty added');
      setShowPenaltyDialog(false);
      setNewPenalty({ car_number: '', penalty_time: '', reason: '' });
      fetchPenalties(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id);
    } catch (error) {
      toast.error('Failed to add penalty');
    } finally {
      setLoading(false);
    }
  };

  const deletePenalty = async (penaltyId) => {
    if (!window.confirm('Remove this penalty?')) return;
    try {
      await axios.delete(`${API}/penalties/${penaltyId}`, getAuthHeaders());
      toast.success('Penalty removed');
      fetchPenalties(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id);
    } catch (error) {
      toast.error('Failed to remove penalty');
    }
  };

  // ==================== STAGE TIME EDITS ====================
  
  const fetchStageTimeEdits = async (eventId) => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/stage-time-edits?event_id=${eventId}`, getAuthHeaders());
      setStageTimeEdits(response.data);
    } catch (error) {
      console.error('Failed to fetch stage time edits');
    }
  };

  const saveStageTimeEdit = async () => {
    if (!editingStageTime || !selectedResultsEvent) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/stage-time-edits`, null, {
        ...getAuthHeaders(),
        params: {
          event_id: selectedResultsEvent.id,
          stage_id: editingStageTime.stage_id,
          car_number: editingStageTime.car_number,
          edited_time: editingStageTime.edited_time,
          original_time: editingStageTime.original_time,
          reason: editingStageTime.reason
        }
      });
      toast.success('Stage time updated');
      setShowEditTimeDialog(false);
      setEditingStageTime(null);
      fetchStageTimeEdits(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id);
    } catch (error) {
      toast.error('Failed to update stage time');
    } finally {
      setLoading(false);
    }
  };

  const deleteStageTimeEdit = async (editId) => {
    if (!window.confirm('Revert to original calculated time?')) return;
    try {
      await axios.delete(`${API}/stage-time-edits/${editId}`, getAuthHeaders());
      toast.success('Reverted to original time');
      fetchStageTimeEdits(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id);
    } catch (error) {
      toast.error('Failed to revert');
    }
  };

  // ==================== LIVE RESULTS ====================
  
  const fetchLiveResults = async (eventId, classFilter = '', showErrors = true) => {
    if (!eventId) return;
    try {
      const params = new URLSearchParams({ event_id: eventId });
      if (classFilter) params.append('class_filter', classFilter);
      const response = await axios.get(`${API}/live-results?${params}`, getAuthHeaders());
      setLiveResults(response.data);
    } catch (error) {
      if (showErrors && error.response?.status !== 403) {
        toast.error('Failed to fetch results');
      }
      console.error('Fetch live results error:', error);
    }
  };

  const exportResultsExcel = async () => {
    if (!selectedResultsEvent) return;
    try {
      const params = new URLSearchParams({ event_id: selectedResultsEvent.id });
      if (selectedClassFilter) params.append('class_filter', selectedClassFilter);
      
      const response = await axios.get(`${API}/live-results/export/excel?${params}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedResultsEvent.name}_results.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Results exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const exportResultsPDF = async (isFinal = false) => {
    if (!liveResults.results || liveResults.results.length === 0) {
      toast.error('No results to export');
      return;
    }
    
    // Build clean table HTML without dropdowns or edit markers
    const stages = liveResults.stages || [];
    const results = liveResults.results || [];
    const isFinalized = isFinal || appSettings.results_finalized;
    const documentTitle = isFinalized ? 'Official Final Results' : 'Official Provisional Results';
    
    const stageHeaders = stages.map(s => `<th style="background:#333;color:white;padding:6px;border:1px solid #ccc;text-align:center;">SS${s.number}</th>`).join('');
    
    const rows = results.map(r => {
      const stageTimeCells = stages.map(s => {
        const time = r.stage_times?.[`stage_${s.number}`] || '-';
        return `<td style="padding:6px;border:1px solid #ccc;text-align:center;font-family:monospace;">${time}</td>`;
      }).join('');
      
      const statusText = r.status === 'dnf' ? 'DNF' : r.status === 'dns' ? 'DNS' : 'Running';
      
      return `<tr>
        <td style="padding:6px;border:1px solid #ccc;font-weight:bold;">${r.position}</td>
        <td style="padding:6px;border:1px solid #ccc;font-family:monospace;font-weight:bold;">${r.car_number}</td>
        <td style="padding:6px;border:1px solid #ccc;">${r.driver}${r.co_driver ? '<br><small style="color:#666;">' + r.co_driver + '</small>' : ''}</td>
        <td style="padding:6px;border:1px solid #ccc;">${r.class_name}${r.clubmans ? ' (C)' : ''}</td>
        <td style="padding:6px;border:1px solid #ccc;font-size:10px;">${r.car_model || '-'}</td>
        ${stageTimeCells}
        <td style="padding:6px;border:1px solid #ccc;text-align:center;color:#f57c00;">${r.total_penalty !== '-' ? r.total_penalty : '-'}</td>
        <td style="padding:6px;border:1px solid #ccc;text-align:center;font-weight:bold;font-family:monospace;">${r.total_time}</td>
        <td style="padding:6px;border:1px solid #ccc;text-align:center;font-family:monospace;">${r.time_to_leader}</td>
        <td style="padding:6px;border:1px solid #ccc;text-align:center;">${statusText}</td>
      </tr>`;
    }).join('');
    
    // Signature section for final results
    const signatureSection = isFinalized ? `
      <div style="margin-top:60px;page-break-inside:avoid;">
        <table style="width:100%;border:none;">
          <tr>
            <td style="width:50%;border:none;padding:20px;">
              <div style="border-bottom:1px solid #333;width:250px;margin-bottom:8px;"></div>
              <p style="margin:0;font-weight:bold;">Clerk of the Course (COC)</p>
              <p style="margin:5px 0 0 0;font-size:11px;color:#666;">Name: _______________________</p>
              <p style="margin:5px 0 0 0;font-size:11px;color:#666;">Date and Time: _______________________</p>
            </td>
            <td style="width:50%;border:none;padding:20px;">
              <div style="border-bottom:1px solid #333;width:250px;margin-bottom:8px;"></div>
              <p style="margin:0;font-weight:bold;">MSA Steward</p>
              <p style="margin:5px 0 0 0;font-size:11px;color:#666;">Name: _______________________</p>
              <p style="margin:5px 0 0 0;font-size:11px;color:#666;">Date and Time: _______________________</p>
            </td>
          </tr>
        </table>
      </div>
    ` : '';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${selectedResultsEvent?.name || 'Rally'} - ${documentTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            h1 { color: #333; margin-bottom: 5px; }
            h2 { color: #333; margin-top: 0; font-size: 16px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <h1>${selectedResultsEvent?.name || 'Rally'}</h1>
          <h2>${documentTitle}</h2>
          <p style="color:#666;font-size:12px;">Generated: ${new Date().toLocaleString()}</p>
          <table>
            <thead>
              <tr>
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;">Pos</th>
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;">No.</th>
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;">Driver/Co-Driver</th>
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;">Class</th>
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;">Car</th>
                ${stageHeaders}
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;text-align:center;">Pen.</th>
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;text-align:center;">Total</th>
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;text-align:center;">Gap</th>
                <th style="background:#333;color:white;padding:6px;border:1px solid #ccc;text-align:center;">Status</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div style="margin-top:20px;font-size:10px;color:#666;">
            <p>(C) = Clubmans Entry</p>
          </div>
          ${signatureSection}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Effect to load results data when event changes
  useEffect(() => {
    if (selectedResultsEvent) {
      fetchEntryList(selectedResultsEvent.id);
      fetchPenalties(selectedResultsEvent.id);
      fetchStageTimeEdits(selectedResultsEvent.id);
      fetchLiveResults(selectedResultsEvent.id, selectedClassFilter);
    }
  }, [selectedResultsEvent]);

  useEffect(() => {
    if (selectedResultsEvent) {
      fetchLiveResults(selectedResultsEvent.id, selectedClassFilter);
    }
  }, [selectedClassFilter]);

  // Auto-refresh live results every 15 seconds when on Results tab
  useEffect(() => {
    if (!selectedResultsEvent) return;
    const interval = setInterval(() => {
      fetchLiveResults(selectedResultsEvent.id, selectedClassFilter, false); // Silent refresh
      fetchPenalties(selectedResultsEvent.id);
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedResultsEvent, selectedClassFilter]);

  const changeUserPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.put(`${API}/users/${selectedUserForPassword.id}/password`, 
        { password: newPassword }, 
        getAuthHeaders()
      );
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setNewPassword('');
      setSelectedUserForPassword(null);
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const updateEvent = async () => {
    if (!editingEvent?.name) {
      toast.error('Please enter event name');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/events/${editingEvent.id}`, {
        name: editingEvent.name,
        description: editingEvent.description,
        start_date: editingEvent.start_date
      }, getAuthHeaders());
      toast.success('Event updated successfully');
      setShowEditEventDialog(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  const updateStage = async () => {
    if (!editingStage?.name) {
      toast.error('Please enter stage name');
      return;
    }

    if (editingStage.stage_number === '' || editingStage.stage_number < 1) {
      toast.error('Please enter a valid stage number');
      return;
    }

    if (editingStage.distance_km === '' || parseFloat(editingStage.distance_km) < 0) {
      toast.error('Please enter a valid distance');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/stages/${editingStage.id}`, {
        name: editingStage.name,
        stage_number: parseInt(editingStage.stage_number),
        distance_km: parseFloat(editingStage.distance_km)
      }, getAuthHeaders());
      toast.success('Stage updated successfully');
      setShowEditStageDialog(false);
      setEditingStage(null);
      if (selectedEvent) fetchStages(selectedEvent.id);
    } catch (error) {
      toast.error('Failed to update stage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] p-2 sm:p-4 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center justify-between sm:block">
            <div>
              <h1 className="text-xl sm:text-3xl font-black text-white">ADMIN DASHBOARD</h1>
              <p className="text-zinc-400 mono-text text-xs sm:text-sm mt-1">{user.name}</p>
            </div>
            {/* Online Status Indicator - Mobile */}
            <div className="flex items-center gap-2 sm:hidden">
              {isOnline ? (
                <div className="flex items-center gap-1 text-green-500">
                  <Wifi size={16} />
                  <span className="text-xs">Online</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-red-500">
                  <WifiOff size={16} />
                  <span className="text-xs">Offline</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 justify-end">
            {/* Online Status Indicator - Desktop */}
            <div className="hidden sm:flex items-center gap-2 mr-2">
              {isOnline ? (
                <div className="flex items-center gap-1 text-green-500">
                  <Wifi size={18} />
                  <span className="text-sm">Online</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-red-500">
                  <WifiOff size={18} />
                  <span className="text-sm">Offline</span>
                </div>
              )}
            </div>
            <NotificationBell user={user} />
            <Button
              onClick={onLogout}
              variant="outline"
              className="border-[#27272a] text-zinc-300 hover:bg-[#27272a] text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-4"
              data-testid="admin-logout-button"
            >
              <LogOut className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">LOGOUT</span>
            </Button>
          </div>
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="bg-[#18181b] border border-[#27272a] w-full justify-start mb-4 sm:mb-6 overflow-x-auto flex-nowrap">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#facc15] data-[state=active]:text-black text-xs sm:text-sm px-2 sm:px-4 flex-shrink-0" data-testid="users-tab">
              <Users className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-[#facc15] data-[state=active]:text-black text-xs sm:text-sm px-2 sm:px-4 flex-shrink-0" data-testid="events-tab">
              <Flag className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">Events</span>
            </TabsTrigger>
            <TabsTrigger value="records" className="data-[state=active]:bg-[#facc15] data-[state=active]:text-black text-xs sm:text-sm px-2 sm:px-4 flex-shrink-0" data-testid="records-tab">
              <RefreshCw className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">Records</span>
            </TabsTrigger>
            <TabsTrigger value="results" className="data-[state=active]:bg-[#facc15] data-[state=active]:text-black text-xs sm:text-sm px-2 sm:px-4 flex-shrink-0" data-testid="results-tab">
              <Trophy className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">Results</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#facc15] data-[state=active]:text-black text-xs sm:text-sm px-2 sm:px-4 flex-shrink-0" data-testid="settings-tab">
              <Settings className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" data-testid="users-content">
            <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-3 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-black uppercase-label text-white">User Management</h2>
                <Button
                  onClick={() => setShowUserDialog(true)}
                  className="bg-[#facc15] text-black hover:bg-[#eab308] text-xs sm:text-sm"
                  data-testid="add-user-button"
                >
                  <Plus className="mr-1 sm:mr-2" size={16} />
                  ADD USER
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      <th className="text-left py-3 px-2 sm:px-4 text-zinc-400 uppercase-label text-xs">Username</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-zinc-400 uppercase-label text-xs">Name</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-zinc-400 uppercase-label text-xs hidden sm:table-cell">Contact</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-zinc-400 uppercase-label text-xs">Role</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-zinc-400 uppercase-label text-xs">Status</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-zinc-400 uppercase-label text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-b border-[#27272a]/50">
                        <td className="py-3 px-2 sm:px-4 text-[#facc15] mono-text text-sm font-bold">{u.username}</td>
                        <td className="py-3 px-2 sm:px-4 text-white text-sm">{u.name}</td>
                        <td className="py-3 px-2 sm:px-4 text-zinc-400 text-xs hidden sm:table-cell">
                          {u.email && <div>{u.email}</div>}
                          {u.phone && <div>{u.phone}</div>}
                          {!u.email && !u.phone && <span className="text-zinc-600">-</span>}
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <div className="flex flex-wrap gap-1">
                            {(u.roles || [u.role] || ['marshal']).map(role => (
                              <span key={role} className="px-2 py-1 bg-[#27272a] text-[#facc15] text-xs uppercase-label rounded">
                                {role}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <Button
                            onClick={() => toggleUserStatus(u.id, u.is_active)}
                            className={`px-2 py-1 text-xs uppercase-label rounded ${
                              u.is_active ? 'bg-[#22c55e] text-black' : 'bg-[#ef4444] text-white'
                            }`}
                          >
                            {u.is_active ? 'Active' : 'Inactive'}
                          </Button>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <div className="flex gap-1">
                            <Button
                              onClick={() => {
                                setEditingUser({...u});
                                setShowEditUserDialog(true);
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-[#3b82f6] hover:bg-[#27272a]"
                              title="Edit user"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              onClick={() => {
                                setSelectedUserForPassword(u);
                                setShowPasswordDialog(true);
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-[#facc15] hover:bg-[#27272a]"
                              title="Change password"
                              data-testid={`change-password-${u.id}`}
                            >
                              🔑
                            </Button>
                            <Button
                              onClick={() => deleteUser(u.id)}
                              size="sm"
                              variant="ghost"
                              className="text-[#ef4444] hover:bg-[#27272a]"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" data-testid="events-content">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Events */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-black uppercase-label text-white">Events</h2>
                  <div className="flex gap-2">
                    {selectedEvents.length > 0 && (
                      <Button
                        onClick={deleteSelectedEvents}
                        className="bg-[#ef4444] text-white hover:bg-[#dc2626]"
                        data-testid="delete-selected-events-button"
                      >
                        <Trash2 className="mr-2" size={16} />
                        DELETE ({selectedEvents.length})
                      </Button>
                    )}
                    <Button
                      onClick={() => setShowEventDialog(true)}
                      className="bg-[#facc15] text-black hover:bg-[#eab308]"
                      data-testid="add-event-button"
                    >
                      <Plus className="mr-2" size={18} />
                      ADD EVENT
                    </Button>
                  </div>
                </div>

                {events.length > 0 && (
                  <div className="mb-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedEvents.length === events.length && events.length > 0}
                      onChange={selectAllEvents}
                      className="rounded bg-[#27272a] border-zinc-600"
                    />
                    <span className="text-xs text-zinc-400">Select All</span>
                  </div>
                )}

                <div className="space-y-3">
                  {events.map((event, index) => (
                    <div
                      key={event.id}
                      className={`p-4 rounded border transition-all ${ 
                        selectedEvent?.id === event.id
                          ? 'border-[#facc15] bg-[#27272a]'
                          : selectedEvents.includes(event.id)
                          ? 'border-[#ef4444]/50 bg-[#27272a]/50'
                          : 'border-[#27272a] hover:border-[#3f3f46]'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        {/* Checkbox */}
                        <div className="flex items-center mr-2 mt-1">
                          <input
                            type="checkbox"
                            checked={selectedEvents.includes(event.id)}
                            onChange={() => toggleEventSelection(event.id)}
                            className="rounded bg-[#27272a] border-zinc-600"
                            data-testid={`select-event-${event.id}`}
                          />
                        </div>
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-0.5 mr-2">
                          <Button
                            onClick={() => reorderEvents(event.id, 'up')}
                            size="sm"
                            variant="ghost"
                            disabled={index === 0}
                            className="h-6 w-6 p-0 text-zinc-500 hover:text-white hover:bg-[#27272a] disabled:opacity-30"
                            title="Move up"
                          >
                            <ChevronUp size={14} />
                          </Button>
                          <Button
                            onClick={() => reorderEvents(event.id, 'down')}
                            size="sm"
                            variant="ghost"
                            disabled={index === events.length - 1}
                            className="h-6 w-6 p-0 text-zinc-500 hover:text-white hover:bg-[#27272a] disabled:opacity-30"
                            title="Move down"
                          >
                            <ChevronDown size={14} />
                          </Button>
                        </div>
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => setSelectedEvent(event)}
                          data-testid={`event-item-${event.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-white">{event.name}</h3>
                            {event.is_current && (
                              <span className="px-2 py-1 bg-[#22c55e] text-black text-xs font-bold rounded uppercase">
                                CURRENT
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-400 mt-1">{event.description}</p>
                          {event.start_date && (
                            <p className="text-xs text-zinc-500 mono-text mt-2">{event.start_date}</p>
                          )}
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button
                            onClick={() => {
                              setEditingEvent({...event});
                              setShowEditEventDialog(true);
                            }}
                            size="sm"
                            variant="ghost"
                            className="text-[#3b82f6] hover:bg-[#27272a]"
                            title="Edit event"
                            data-testid={`edit-event-${event.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          {!event.is_current && (
                            <Button
                              onClick={() => setCurrentEvent(event.id)}
                              size="sm"
                              variant="ghost"
                              className="text-[#22c55e] hover:bg-[#27272a]"
                              title="Set as current event"
                              data-testid={`set-current-${event.id}`}
                            >
                              <Flag size={16} />
                            </Button>
                          )}
                          <Button
                            onClick={() => deleteEvent(event.id)}
                            size="sm"
                            variant="ghost"
                            className="text-[#ef4444] hover:bg-[#27272a]"
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stages */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-black uppercase-label text-white">Stages</h2>
                  <div className="flex gap-2">
                    {selectedStages.length > 0 && (
                      <Button
                        onClick={deleteSelectedStages}
                        className="bg-[#ef4444] text-white hover:bg-[#dc2626]"
                        data-testid="delete-selected-stages-button"
                      >
                        <Trash2 className="mr-2" size={16} />
                        DELETE ({selectedStages.length})
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        // Set event_id when opening dialog
                        setNewStage(prev => ({ ...prev, event_id: selectedEvent?.id || '' }));
                        setShowStageDialog(true);
                      }}
                      disabled={!selectedEvent}
                      className="bg-[#facc15] text-black hover:bg-[#eab308] disabled:opacity-50"
                      data-testid="add-stage-button"
                    >
                      <Plus className="mr-2" size={18} />
                      ADD STAGE
                    </Button>
                  </div>
                </div>

                {selectedEvent && (
                  <div className="mb-4 p-3 bg-[#27272a] rounded">
                    <p className="text-xs uppercase-label text-zinc-400">Selected Event</p>
                    <p className="text-white font-bold">{selectedEvent.name}</p>
                  </div>
                )}

                {stages.length > 0 && (
                  <div className="mb-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedStages.length === stages.length && stages.length > 0}
                      onChange={selectAllStages}
                      className="rounded bg-[#27272a] border-zinc-600"
                    />
                    <span className="text-xs text-zinc-400">Select All</span>
                  </div>
                )}

                <div className="space-y-2">
                  {stages.map((stage, index) => (
                    <div
                      key={stage.id}
                      className={`p-3 rounded border ${selectedStages.includes(stage.id) ? 'border-[#ef4444]/50 bg-[#27272a]/50' : 'border-[#27272a] bg-[#09090b]'}`}
                      data-testid={`stage-item-${stage.id}`}
                    >
                      <div className="flex items-center justify-between">
                        {/* Checkbox */}
                        <div className="flex items-center mr-2">
                          <input
                            type="checkbox"
                            checked={selectedStages.includes(stage.id)}
                            onChange={() => toggleStageSelection(stage.id)}
                            className="rounded bg-[#27272a] border-zinc-600"
                            data-testid={`select-stage-${stage.id}`}
                          />
                        </div>
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-0.5 mr-2">
                          <Button
                            onClick={() => reorderStages(stage.id, 'up')}
                            size="sm"
                            variant="ghost"
                            disabled={index === 0}
                            className="h-5 w-5 p-0 text-zinc-500 hover:text-white hover:bg-[#27272a] disabled:opacity-30"
                            title="Move up"
                          >
                            <ChevronUp size={12} />
                          </Button>
                          <Button
                            onClick={() => reorderStages(stage.id, 'down')}
                            size="sm"
                            variant="ghost"
                            disabled={index === stages.length - 1}
                            className="h-5 w-5 p-0 text-zinc-500 hover:text-white hover:bg-[#27272a] disabled:opacity-30"
                            title="Move down"
                          >
                            <ChevronDown size={12} />
                          </Button>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`font-bold mono-text ${stage.is_cancelled ? 'text-red-400 line-through' : 'text-[#facc15]'}`}>
                                {stage.stage_type === 'stage' ? `SS${stage.stage_number}` : stage.stage_type === 'service_park' ? 'SERVICE' : 'SECTION'}
                                {stage.is_cancelled && <span className="ml-2 text-xs text-red-400 no-underline">(CANCELLED)</span>}
                              </span>
                              <p className={`${stage.is_cancelled ? 'text-zinc-500 line-through' : 'text-white'}`}>{stage.name}</p>
                            </div>
                            {stage.distance_km > 0 && (
                              <span className="text-zinc-400 text-sm mono-text mr-2">{stage.distance_km}km</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            onClick={() => {
                              setEditingStage({...stage});
                              setShowEditStageDialog(true);
                            }}
                            size="sm"
                            variant="ghost"
                            className="text-[#3b82f6] hover:bg-[#27272a]"
                            title="Edit stage"
                            data-testid={`edit-stage-${stage.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            onClick={() => deleteStage(stage.id)}
                            size="sm"
                            variant="ghost"
                            className="text-[#ef4444] hover:bg-[#27272a]"
                            data-testid={`delete-stage-${stage.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Records Tab */}
          <TabsContent value="records" data-testid="records-content">
            <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-3 sm:p-6">
              <div className="flex flex-col gap-3 mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-black uppercase-label text-white">
                  TIMING RECORDS ({timingRecords.length})
                </h2>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={selectedRecordEvent?.id || ''}
                    onChange={(e) => {
                      const event = events.find(ev => ev.id === e.target.value);
                      setSelectedRecordEvent(event || null);
                      fetchTimingRecords();
                    }}
                    className="bg-[#27272a] border-[#27272a] text-white rounded p-2 w-full sm:min-w-[200px]"
                    data-testid="record-event-filter-select"
                  >
                    <option value="">All Events</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>{event.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 sm:flex gap-2">
                  <Button
                    onClick={() => fetchTimingRecords(selectedRecordEvent)}
                    className="bg-[#27272a] text-white hover:bg-[#3f3f46] text-xs sm:text-sm"
                    data-testid="refresh-records-button"
                    disabled={loading}
                  >
                    <RefreshCw className={`mr-1 sm:mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
                    {loading ? '...' : 'REFRESH'}
                  </Button>
                  <Button
                    onClick={exportToExcel}
                    className="bg-[#22c55e] text-white hover:bg-[#16a34a] text-xs sm:text-sm"
                    data-testid="export-excel-button"
                  >
                    <FileSpreadsheet className="mr-1 sm:mr-2" size={16} />
                    EXCEL
                  </Button>
                  <Button
                    onClick={exportPhotos}
                    className="bg-[#3b82f6] text-white hover:bg-[#2563eb] text-xs sm:text-sm col-span-2 sm:col-span-1"
                    data-testid="export-photos-button"
                  >
                    <Download className="mr-1 sm:mr-2" size={16} />
                    PHOTOS ({photoCount})
                  </Button>
                  {selectedRecords.length > 0 && (
                    <Button
                      onClick={deleteSelectedRecords}
                      className="bg-[#ef4444] text-white hover:bg-[#dc2626] text-xs sm:text-sm col-span-2 sm:col-span-1"
                      data-testid="delete-selected-records-button"
                    >
                      <Trash2 className="mr-1 sm:mr-2" size={16} />
                      DELETE ({selectedRecords.length})
                    </Button>
                  )}
                </div>
              </div>

              {timingRecords.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-zinc-400">No timing records found</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupRecordsByCar()).map(([carNumber, carRecords]) => (
                    <div key={carNumber} className="border border-[#27272a] rounded-lg p-4 card-strip-yellow">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-2xl font-black text-[#facc15] mono-text">CAR #{carNumber}</h3>
                        <span className="text-sm text-zinc-400">{carRecords.length} records</span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-[#27272a]">
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">
                                <input
                                  type="checkbox"
                                  checked={carRecords.length > 0 && carRecords.every(r => selectedRecords.includes(r.id))}
                                  onChange={() => selectAllRecordsForCar(carRecords)}
                                  className="rounded bg-[#27272a] border-zinc-600"
                                  title="Select all for this car"
                                />
                              </th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Stage</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Type</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Timestamp</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Recorded By</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Photo</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Synced</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {carRecords.map(record => {
                              const stageInfo = getStageDisplay(record.stage_id);
                              return (
                              <tr key={record.id} className={`border-b border-[#27272a]/30 ${selectedRecords.includes(record.id) ? 'bg-[#27272a]/50' : ''}`}>
                                <td className="py-2 px-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedRecords.includes(record.id)}
                                    onChange={() => toggleRecordSelection(record.id)}
                                    className="rounded bg-[#27272a] border-zinc-600"
                                    data-testid={`select-record-${record.id}`}
                                  />
                                </td>
                                <td className="py-2 px-3">
                                  <div>
                                    <span className="text-xs font-bold text-[#facc15]">{stageInfo.label}</span>
                                    <span className="text-xs text-zinc-400 ml-1">- {stageInfo.name}</span>
                                  </div>
                                </td>
                                <td className="py-2 px-3">
                                  <span className="text-xs uppercase-label text-zinc-300">
                                    {record.timing_type.replace('_', ' ')}
                                  </span>
                                  {record.description && (
                                    <div className="text-xs text-zinc-500 italic mt-1">"{record.description}"</div>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-white mono-text text-sm">
                                  {new Date(record.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })} {new Date(record.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </td>
                                <td className="py-2 px-3 text-zinc-400 text-sm">{record.recorded_by_name}</td>
                                <td className="py-2 px-3">
                                  {record.photo_data ? (
                                    <img
                                      src={record.photo_data}
                                      alt="Car"
                                      className="w-16 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={() => {
                                        setSelectedPhotoRecord(record);
                                        setShowPhotoDialog(true);
                                      }}
                                      data-testid={`photo-thumbnail-${record.id}`}
                                    />
                                  ) : (
                                    <span className="text-xs text-zinc-500">No photo</span>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {record.synced ? (
                                    <span className="px-2 py-1 bg-[#22c55e]/20 text-[#22c55e] text-xs rounded">
                                      Synced
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 bg-[#facc15]/20 text-[#facc15] text-xs rounded">
                                      Pending
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  <Button
                                    onClick={() => deleteTimingRecord(record.id)}
                                    size="sm"
                                    variant="ghost"
                                    className="text-[#ef4444] hover:bg-[#27272a]"
                                    data-testid={`delete-record-${record.id}`}
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </td>
                              </tr>
                            );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" data-testid="results-content">
            <div className="space-y-6">
              {/* Event Selection & Controls */}
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase">Select Event</Label>
                    <select
                      value={selectedResultsEvent?.id || ''}
                      onChange={(e) => {
                        const event = events.find(ev => ev.id === e.target.value);
                        setSelectedResultsEvent(event || null);
                      }}
                      className="bg-[#27272a] border-[#27272a] text-white rounded p-2 w-full min-w-[200px] mt-1"
                      data-testid="results-event-select"
                    >
                      <option value="">Select Event...</option>
                      {events.map(event => (
                        <option key={event.id} value={event.id}>{event.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedResultsEvent && (
                    <>
                      <div className="flex gap-2 ml-auto">
                        <Button
                          onClick={() => fetchLiveResults(selectedResultsEvent.id, selectedClassFilter)}
                          className="bg-[#27272a] text-white hover:bg-[#3f3f46]"
                          disabled={loading}
                          data-testid="refresh-results-btn"
                        >
                          <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
                          Refresh
                        </Button>
                        <Button
                          onClick={exportResultsExcel}
                          className="bg-[#22c55e] text-white hover:bg-[#16a34a]"
                          data-testid="export-results-excel-btn"
                        >
                          <FileSpreadsheet className="mr-2" size={16} />
                          Excel
                        </Button>
                        <Button
                          onClick={() => exportResultsPDF(false)}
                          className="bg-[#3b82f6] text-white hover:bg-[#2563eb]"
                          data-testid="export-results-pdf-btn"
                        >
                          <Download className="mr-2" size={16} />
                          PDF
                        </Button>
                        <Button
                          onClick={() => {
                            if (!appSettings.results_finalized) {
                              if (window.confirm('Are you sure you want to finalize results? This will mark the results as official and final.')) {
                                const newSettings = { ...appSettings, results_finalized: true };
                                setAppSettings(newSettings);
                                updateAppSettings(newSettings);
                                exportResultsPDF(true);
                              }
                            } else {
                              exportResultsPDF(true);
                            }
                          }}
                          className={appSettings.results_finalized ? 'bg-[#facc15] text-black hover:bg-[#eab308]' : 'bg-[#ef4444] text-white hover:bg-[#dc2626]'}
                          data-testid="finalize-results-btn"
                        >
                          <Flag className="mr-2" size={16} />
                          {appSettings.results_finalized ? 'Final PDF' : 'Finalize'}
                        </Button>
                        {appSettings.results_finalized && (
                          <Button
                            onClick={() => {
                              if (window.confirm('Revert to provisional results? This will allow further edits.')) {
                                const newSettings = { ...appSettings, results_finalized: false };
                                setAppSettings(newSettings);
                                updateAppSettings(newSettings);
                                toast.success('Results reverted to provisional');
                              }
                            }}
                            variant="outline"
                            className="border-zinc-600 text-zinc-400 hover:bg-zinc-700"
                            data-testid="unfinalize-btn"
                          >
                            Undo Finalize
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                
                {selectedResultsEvent && (
                  <p className="text-[#facc15] font-bold">
                    Managing: {selectedResultsEvent.name}
                  </p>
                )}
              </div>

              {selectedResultsEvent && (
                <>
                  {/* Entry List Section */}
                  <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Users size={20} className="text-[#facc15]" />
                        Entry List ({entryList.length})
                      </h3>
                      <div className="flex gap-2">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleEntryListUpload}
                          accept=".xlsx,.xls"
                          className="hidden"
                          data-testid="entry-list-upload-input"
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          className="bg-[#facc15] text-black hover:bg-[#eab308]"
                          disabled={loading}
                          data-testid="upload-entry-list-btn"
                        >
                          <Upload className="mr-2" size={16} />
                          Upload Excel
                        </Button>
                        <Button
                          onClick={() => {
                            setEditingEntry({
                              car_number: '',
                              entrant: '',
                              driver: '',
                              co_driver: '',
                              class_name: '',
                              drive_type: '2WD',
                              clubmans: false,
                              car_model: '',
                              entry_status: 'running'
                            });
                            setShowEntryDialog(true);
                          }}
                          className="bg-[#27272a] text-white hover:bg-[#3f3f46]"
                          data-testid="add-entry-btn"
                        >
                          <Plus className="mr-2" size={16} />
                          Add Entry
                        </Button>
                      </div>
                    </div>

                    {entryList.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        <p>No entries yet. Upload an Excel file or add entries manually.</p>
                        <p className="text-xs mt-2">Excel columns: Car No, Entrant, Driver, Co-Driver, Class, 2WD/4WD, Clubmans, Car/Model</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#27272a]">
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Car No</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Driver</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Co-Driver</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Class</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Drive</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Clubmans</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Car</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Status</th>
                              <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entryList.map(entry => (
                              <tr key={entry.id} className="border-b border-[#27272a]/50 hover:bg-[#27272a]/30">
                                <td className="py-2 px-3 text-[#facc15] font-mono font-bold">{entry.car_number}</td>
                                <td className="py-2 px-3 text-white">{entry.driver}</td>
                                <td className="py-2 px-3 text-zinc-400">{entry.co_driver || '-'}</td>
                                <td className="py-2 px-3"><span className="px-2 py-1 bg-[#27272a] rounded text-xs">{entry.class_name}</span></td>
                                <td className="py-2 px-3 text-zinc-400">{entry.drive_type}</td>
                                <td className="py-2 px-3">{entry.clubmans ? <span className="text-blue-400">Y</span> : <span className="text-zinc-600">N</span>}</td>
                                <td className="py-2 px-3 text-zinc-400 text-xs">{entry.car_model}</td>
                                <td className="py-2 px-3">
                                  <select
                                    value={entry.entry_status}
                                    onChange={(e) => updateEntryStatus(entry.id, e.target.value)}
                                    className={`bg-[#27272a] text-xs rounded px-2 py-1 ${
                                      entry.entry_status === 'dnf' ? 'text-red-400' : 
                                      entry.entry_status === 'dns' ? 'text-yellow-400' : 
                                      'text-green-400'
                                    }`}
                                  >
                                    <option value="running">Running</option>
                                    <option value="dnf">DNF</option>
                                    <option value="dns">DNS</option>
                                  </select>
                                </td>
                                <td className="py-2 px-3">
                                  <div className="flex gap-1">
                                    <Button
                                      onClick={() => {
                                        setEditingEntry({...entry});
                                        setShowEntryDialog(true);
                                      }}
                                      size="sm"
                                      variant="ghost"
                                      className="text-[#3b82f6] hover:bg-[#27272a]"
                                    >
                                      <Edit size={14} />
                                    </Button>
                                    <Button
                                      onClick={() => deleteEntry(entry.id)}
                                      size="sm"
                                      variant="ghost"
                                      className="text-[#ef4444] hover:bg-[#27272a]"
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Penalties Section */}
                  <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <AlertTriangle size={20} className="text-orange-400" />
                        Penalties ({penalties.length})
                      </h3>
                      <Button
                        onClick={() => {
                          setNewPenalty({ car_number: '', penalty_time: '', reason: '' });
                          setShowPenaltyDialog(true);
                        }}
                        className="bg-orange-600 text-white hover:bg-orange-700"
                        data-testid="add-penalty-btn"
                      >
                        <Plus className="mr-2" size={16} />
                        Add Penalty
                      </Button>
                    </div>

                    {penalties.length === 0 ? (
                      <div className="text-center py-4 text-zinc-500">
                        <p>No penalties recorded</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {penalties.map(penalty => (
                          <div key={penalty.id} className="flex items-center justify-between bg-[#27272a] rounded p-3">
                            <div className="flex items-center gap-4">
                              <span className="text-[#facc15] font-mono font-bold">#{penalty.car_number}</span>
                              <span className="text-orange-400 font-mono">+{penalty.penalty_time}</span>
                              <span className="text-zinc-400 text-sm">{penalty.reason || 'No reason'}</span>
                              <span className="text-zinc-500 text-xs">by {penalty.added_by_name}</span>
                            </div>
                            <Button
                              onClick={() => deletePenalty(penalty.id)}
                              size="sm"
                              variant="ghost"
                              className="text-[#ef4444] hover:bg-[#ef4444]/20"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Live Results Preview */}
                  <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Trophy size={20} className="text-[#facc15]" />
                        Live Results Preview
                      </h3>
                      <div className="flex items-center gap-3">
                        <select
                          value={selectedClassFilter}
                          onChange={(e) => setSelectedClassFilter(e.target.value)}
                          className="bg-[#27272a] border-[#27272a] text-white rounded p-2 text-sm"
                        >
                          <option value="">All Classes</option>
                          {liveResults.classes?.map(cls => (
                            <option key={cls} value={cls}>{cls}</option>
                          ))}
                        </select>
                        <Button
                          onClick={() => navigate('/live-results')}
                          variant="link"
                          className="text-[#facc15] text-sm hover:underline p-0 h-auto"
                          data-testid="open-full-view-btn"
                        >
                          Open Full View →
                        </Button>
                      </div>
                    </div>

                    {liveResults.results?.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        <p>No results available. Add entries and record timing data to see results.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table id="results-table" className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#27272a]">
                              <th className="text-left py-2 px-2 text-[#facc15] text-xs">Pos</th>
                              <th className="text-left py-2 px-2 text-[#facc15] text-xs">Car</th>
                              <th className="text-left py-2 px-2 text-[#facc15] text-xs">Driver</th>
                              <th className="text-left py-2 px-2 text-[#facc15] text-xs">Class</th>
                              {liveResults.stages?.map(stage => (
                                <th key={stage.id} className="text-center py-2 px-2 text-[#facc15] text-xs whitespace-nowrap">
                                  SS{stage.number}
                                </th>
                              ))}
                              <th className="text-center py-2 px-2 text-[#facc15] text-xs">Pen.</th>
                              <th className="text-center py-2 px-2 text-[#facc15] text-xs">Total</th>
                              <th className="text-center py-2 px-2 text-[#facc15] text-xs">Gap</th>
                              <th className="text-center py-2 px-2 text-[#facc15] text-xs">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {liveResults.results?.slice(0, 10).map((result, idx) => (
                              <tr 
                                key={result.car_number} 
                                className={`border-b border-[#27272a]/50 ${idx === 0 ? 'bg-[#facc15]/10' : ''} ${result.status === 'dnf' || result.status === 'dns' ? 'opacity-50' : ''}`}
                              >
                                <td className="py-2 px-2 font-bold text-white">{result.position}</td>
                                <td className="py-2 px-2 text-[#facc15] font-mono font-bold">{result.car_number}</td>
                                <td className="py-2 px-2 text-white">{result.driver}</td>
                                <td className="py-2 px-2">
                                  <span className="text-xs">{result.class_name}</span>
                                  {result.clubmans && <span className="ml-1 text-blue-400 text-xs">C</span>}
                                </td>
                                {liveResults.stages?.map(stage => {
                                  const stageKey = `stage_${stage.number}`;
                                  const time = result.stage_times?.[stageKey] || '-';
                                  const isEdited = result.stage_times_edited?.[stageKey];
                                  return (
                                    <td 
                                      key={stage.id} 
                                      className={`py-2 px-2 text-center font-mono text-xs ${isEdited ? 'bg-blue-500/20 text-blue-300' : 'text-white'}`}
                                    >
                                      {time}
                                    </td>
                                  );
                                })}
                                <td className="py-2 px-2 text-center font-mono text-orange-400 text-xs">
                                  {result.total_penalty !== '-' ? result.total_penalty : '-'}
                                </td>
                                <td className="py-2 px-2 text-center font-mono font-bold text-white text-xs">
                                  {result.total_time}
                                </td>
                                <td className="py-2 px-2 text-center font-mono text-zinc-400 text-xs">
                                  {result.time_to_leader}
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    result.status === 'dnf' ? 'bg-red-500/20 text-red-400' : 
                                    result.status === 'dns' ? 'bg-yellow-500/20 text-yellow-400' : 
                                    'bg-green-500/20 text-green-400'
                                  }`}>
                                    {result.status === 'dnf' ? 'DNF' : result.status === 'dns' ? 'DNS' : 'Running'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {liveResults.results?.length > 10 && (
                          <p className="text-center text-zinc-500 text-xs mt-2">
                            Showing top 10 of {liveResults.results.length} results
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" data-testid="settings-content">
            <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6">
              <h2 className="text-xl font-black uppercase-label text-white mb-6">Settings</h2>

              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-[#27272a] rounded">
                  <div>
                    <h3 className="font-bold text-white">Live Timing Display</h3>
                    <p className="text-sm text-zinc-400 mt-1">Enable/disable public live timing view</p>
                  </div>
                  <Button
                    onClick={toggleLiveTiming}
                    className={liveTimingEnabled ? 'bg-[#22c55e] hover:bg-[#16a34a]' : 'bg-[#ef4444] hover:bg-[#dc2626]'}
                    data-testid="toggle-live-timing-button"
                  >
                    {liveTimingEnabled ? 'ENABLED' : 'DISABLED'}
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 bg-[#27272a] rounded">
                  <div>
                    <h3 className="font-bold text-white">Live Results (Admin/Scorer)</h3>
                    <p className="text-sm text-zinc-400 mt-1">Enable live results view for admin and scorer users</p>
                  </div>
                  <Button
                    onClick={() => {
                      const newValue = !appSettings.live_results_enabled;
                      setAppSettings({ ...appSettings, live_results_enabled: newValue });
                      updateAppSettings({ ...appSettings, live_results_enabled: newValue });
                    }}
                    className={appSettings.live_results_enabled ? 'bg-[#22c55e] hover:bg-[#16a34a]' : 'bg-[#ef4444] hover:bg-[#dc2626]'}
                    data-testid="toggle-live-results-button"
                  >
                    {appSettings.live_results_enabled ? 'ENABLED' : 'DISABLED'}
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 bg-[#27272a] rounded">
                  <div>
                    <h3 className="font-bold text-white">Public Results Page</h3>
                    <p className="text-sm text-zinc-400 mt-1">Enable public access to results (can be linked to custom domain)</p>
                    {appSettings.public_results_enabled && (
                      <a 
                        href="/results" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-[#facc15] hover:underline mt-1 block"
                      >
                        View public results page →
                      </a>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      const newValue = !appSettings.public_results_enabled;
                      setAppSettings({ ...appSettings, public_results_enabled: newValue });
                      updateAppSettings({ ...appSettings, public_results_enabled: newValue });
                    }}
                    className={appSettings.public_results_enabled ? 'bg-[#22c55e] hover:bg-[#16a34a]' : 'bg-[#ef4444] hover:bg-[#dc2626]'}
                    data-testid="toggle-public-results-button"
                  >
                    {appSettings.public_results_enabled ? 'ENABLED' : 'DISABLED'}
                  </Button>
                </div>

                <div className="p-4 bg-[#27272a] rounded">
                  <h3 className="font-bold text-white mb-4">Server Time Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-zinc-300 uppercase-label">Timezone</Label>
                      <select
                        value={appSettings.timezone}
                        onChange={(e) => setAppSettings({ ...appSettings, timezone: e.target.value })}
                        className="w-full mt-2 bg-[#18181b] border-[#27272a] text-white rounded p-2"
                        data-testid="timezone-select"
                      >
                        <option value="UTC">UTC (GMT+0)</option>
                        <option value="Africa/Johannesburg">South Africa (GMT+2)</option>
                        <option value="Europe/London">London (GMT+0/+1)</option>
                        <option value="Europe/Paris">Paris (GMT+1/+2)</option>
                        <option value="America/New_York">New York (GMT-5/-4)</option>
                        <option value="America/Los_Angeles">Los Angeles (GMT-8/-7)</option>
                        <option value="Asia/Tokyo">Tokyo (GMT+9)</option>
                        <option value="Australia/Sydney">Sydney (GMT+10/+11)</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-zinc-300 uppercase-label">Sync Interval (Hours)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="24"
                        value={appSettings.sync_interval_hours}
                        onChange={(e) => setAppSettings({ ...appSettings, sync_interval_hours: parseInt(e.target.value) })}
                        className="mt-2 bg-[#18181b] border-[#27272a] text-white"
                        data-testid="sync-interval-input"
                      />
                      <p className="text-xs text-zinc-500 mt-1">
                        How often mobile apps should sync time with server (currently: {appSettings.sync_interval_hours} hour{appSettings.sync_interval_hours !== 1 ? 's' : ''})
                      </p>
                    </div>
                    <Button
                      onClick={updateAppSettings}
                      className="bg-[#facc15] text-black hover:bg-[#eab308] font-black"
                      data-testid="save-app-settings-button"
                    >
                      SAVE SETTINGS
                    </Button>
                  </div>
                </div>

                {/* Clear All Notifications */}
                <div className="p-4 bg-[#27272a] rounded">
                  <h3 className="font-bold text-white mb-4">Notification Management</h3>
                  <p className="text-sm text-zinc-400 mb-4">
                    Clear all notifications for all users. This action cannot be undone.
                  </p>
                  <Button
                    onClick={async () => {
                      if (!window.confirm('Are you sure you want to clear ALL notifications for ALL users? This cannot be undone.')) return;
                      try {
                        await axios.delete(`${API}/notifications`, getAuthHeaders());
                        toast.success('All notifications cleared');
                      } catch (error) {
                        toast.error('Failed to clear notifications');
                      }
                    }}
                    className="bg-red-600 text-white hover:bg-red-700 font-black"
                    data-testid="clear-all-notifications-button"
                  >
                    CLEAR ALL NOTIFICATIONS
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">CREATE USER</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300 uppercase-label">Username <span className="text-red-500">*</span></Label>
              <Input
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                placeholder="e.g., john_marshal"
                data-testid="new-user-username-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Full Name <span className="text-red-500">*</span></Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="new-user-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Password <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="new-user-password-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 uppercase-label text-xs">Email (Optional)</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                  placeholder="email@example.com"
                  data-testid="new-user-email-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300 uppercase-label text-xs">Phone (Optional)</Label>
                <Input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                  placeholder="+27 123 4567"
                  data-testid="new-user-phone-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Roles <span className="text-red-500">*</span></Label>
              <p className="text-xs text-zinc-500 mt-1 mb-2">Select one or more roles for this user</p>
              <div className="space-y-2 mt-2">
                {[
                  { value: 'marshal', label: 'Marshal / Timer' },
                  { value: 'scorer', label: 'Scorer' },
                  { value: 'admin', label: 'Administrator' }
                ].map(roleOption => (
                  <label key={roleOption.value} className="flex items-center gap-3 p-2 bg-[#27272a] rounded cursor-pointer hover:bg-[#3f3f46]">
                    <input
                      type="checkbox"
                      checked={newUser.roles?.includes(roleOption.value)}
                      onChange={(e) => {
                        const currentRoles = newUser.roles || [];
                        if (e.target.checked) {
                          setNewUser({ ...newUser, roles: [...currentRoles, roleOption.value] });
                        } else {
                          setNewUser({ ...newUser, roles: currentRoles.filter(r => r !== roleOption.value) });
                        }
                      }}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-[#facc15] focus:ring-[#facc15]"
                      data-testid={`new-user-role-${roleOption.value}`}
                    />
                    <span className="text-white">{roleOption.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button
              onClick={createUser}
              disabled={loading || !newUser.username || !newUser.name || !newUser.password || !newUser.roles?.length}
              className="w-full bg-[#facc15] text-black hover:bg-[#eab308] h-12 font-black uppercase disabled:opacity-50"
              data-testid="create-user-submit-button"
            >
              {loading ? 'CREATING...' : 'CREATE USER'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">EDIT USER</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300 uppercase-label">Username <span className="text-red-500">*</span></Label>
              <Input
                value={editingUser?.username || ''}
                onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="edit-user-username-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Full Name <span className="text-red-500">*</span></Label>
              <Input
                value={editingUser?.name || ''}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="edit-user-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 uppercase-label text-xs">Email (Optional)</Label>
                <Input
                  type="email"
                  value={editingUser?.email || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                  data-testid="edit-user-email-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300 uppercase-label text-xs">Phone (Optional)</Label>
                <Input
                  type="tel"
                  value={editingUser?.phone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                  data-testid="edit-user-phone-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Roles <span className="text-red-500">*</span></Label>
              <p className="text-xs text-zinc-500 mt-1 mb-2">Select one or more roles for this user</p>
              <div className="space-y-2 mt-2">
                {[
                  { value: 'marshal', label: 'Marshal / Timer' },
                  { value: 'scorer', label: 'Scorer' },
                  { value: 'admin', label: 'Administrator' }
                ].map(roleOption => {
                  // Get current roles array, fallback to single role or marshal
                  const currentRoles = editingUser?.roles || (editingUser?.role ? [editingUser.role] : ['marshal']);
                  return (
                    <label key={roleOption.value} className="flex items-center gap-3 p-2 bg-[#27272a] rounded cursor-pointer hover:bg-[#3f3f46]">
                      <input
                        type="checkbox"
                        checked={currentRoles.includes(roleOption.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditingUser({ ...editingUser, roles: [...currentRoles, roleOption.value] });
                          } else {
                            setEditingUser({ ...editingUser, roles: currentRoles.filter(r => r !== roleOption.value) });
                          }
                        }}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-[#facc15] focus:ring-[#facc15]"
                        data-testid={`edit-user-role-${roleOption.value}`}
                      />
                      <span className="text-white">{roleOption.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowEditUserDialog(false);
                  setEditingUser(null);
                }}
                variant="outline"
                className="flex-1 border-[#27272a] text-zinc-300"
              >
                CANCEL
              </Button>
              <Button
                onClick={updateUser}
                disabled={loading || !editingUser?.username || !editingUser?.name || !(editingUser?.roles?.length || editingUser?.role)}
                className="flex-1 bg-[#facc15] text-black hover:bg-[#eab308] font-black disabled:opacity-50"
                data-testid="update-user-submit-button"
              >
                {loading ? 'UPDATING...' : 'UPDATE USER'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">CREATE EVENT</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300 uppercase-label">Event Name</Label>
              <Input
                value={newEvent.name}
                onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="new-event-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Description</Label>
              <Input
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="new-event-description-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Start Date</Label>
              <Input
                type="date"
                value={newEvent.start_date}
                onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
              />
            </div>
            <Button
              onClick={createEvent}
              disabled={loading}
              className="w-full bg-[#facc15] text-black hover:bg-[#eab308] h-12 font-black uppercase"
              data-testid="create-event-submit-button"
            >
              {loading ? 'CREATING...' : 'CREATE EVENT'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stage Dialog */}
      <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">ADD STAGE / SERVICE PARK</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Show which event the stage will be added to */}
            <div className="p-3 bg-[#27272a] rounded border border-[#3f3f46]">
              <p className="text-xs uppercase-label text-zinc-400">Adding to Event</p>
              <p className="text-white font-bold">{selectedEvent?.name || 'No event selected'}</p>
            </div>
            {/* Stage Type Selection */}
            <div>
              <Label className="text-zinc-300 uppercase-label">Type <span className="text-red-500">*</span></Label>
              <select
                value={newStage.stage_type}
                onChange={(e) => setNewStage({ ...newStage, stage_type: e.target.value })}
                className="mt-2 w-full bg-[#27272a] border border-[#27272a] text-white rounded-md px-3 py-2"
                data-testid="new-stage-type-select"
              >
                <option value="stage">Stage (SS)</option>
                <option value="service_park">Service Park</option>
                <option value="rally_start">Rally Section</option>
              </select>
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">
                {newStage.stage_type === 'stage' ? 'Stage Name' : newStage.stage_type === 'service_park' ? 'Service Park Name' : 'Rally Section Name'} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={newStage.name}
                onChange={(e) => setNewStage({ ...newStage, name: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                placeholder={newStage.stage_type === 'stage' ? 'e.g., Mountain Pass' : newStage.stage_type === 'service_park' ? 'e.g., Main Service Area' : 'e.g., Ceremonial Start'}
                data-testid="new-stage-name-input"
              />
            </div>
            {/* Stage Number - only for stage type */}
            {newStage.stage_type === 'stage' && (
              <div>
                <Label className="text-zinc-300 uppercase-label">Stage Number <span className="text-red-500">*</span></Label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={newStage.stage_number}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setNewStage({ ...newStage, stage_number: val === '' ? '' : parseInt(val) });
                  }}
                  className="mt-2 w-full bg-[#27272a] border border-[#27272a] text-white rounded-md px-3 py-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="e.g., 1"
                  data-testid="new-stage-number-input"
                />
              </div>
            )}
            <div>
              <Label className="text-zinc-300 uppercase-label">Distance (km) <span className="text-red-500">*</span></Label>
              <input
                type="text"
                inputMode="decimal"
                value={newStage.distance_km}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, '');
                  // Only allow one decimal point
                  const parts = val.split('.');
                  const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : val;
                  setNewStage({ ...newStage, distance_km: sanitized === '' ? '' : sanitized });
                }}
                className="mt-2 w-full bg-[#27272a] border border-[#27272a] text-white rounded-md px-3 py-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="e.g., 12.5"
              />
            </div>
            <Button
              onClick={createStage}
              disabled={loading || !newStage.name || (newStage.stage_type === 'stage' && newStage.stage_number === '') || newStage.distance_km === ''}
              className="w-full bg-[#facc15] text-black hover:bg-[#eab308] h-12 font-black uppercase disabled:opacity-50"
              data-testid="create-stage-submit-button"
            >
              {loading ? 'CREATING...' : `CREATE ${newStage.stage_type === 'stage' ? 'STAGE' : newStage.stage_type === 'service_park' ? 'SERVICE PARK' : 'RALLY SECTION'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo View Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">
              CAR #{selectedPhotoRecord?.car_number} PHOTO
            </DialogTitle>
          </DialogHeader>
          {selectedPhotoRecord?.photo_data && (
            <div className="mt-4">
              <img
                src={selectedPhotoRecord.photo_data}
                alt={`Car ${selectedPhotoRecord.car_number}`}
                className="w-full rounded"
              />
              <div className="mt-4 p-3 bg-[#27272a] rounded space-y-2">
                <p className="text-sm">
                  <span className="text-zinc-400">Type:</span>{' '}
                  <span className="text-white uppercase-label">{selectedPhotoRecord.timing_type.replace('_', ' ')}</span>
                </p>
                <p className="text-sm">
                  <span className="text-zinc-400">Time:</span>{' '}
                  <span className="text-white mono-text">{new Date(selectedPhotoRecord.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })} {new Date(selectedPhotoRecord.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                </p>
                <p className="text-sm">
                  <span className="text-zinc-400">Recorded By:</span>{' '}
                  <span className="text-white">{selectedPhotoRecord.recorded_by_name}</span>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">
              CHANGE PASSWORD
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300 uppercase-label">User</Label>
              <div className="mt-2 p-3 bg-[#27272a] rounded">
                <p className="text-white font-bold">{selectedUserForPassword?.name}</p>
                <p className="text-sm text-zinc-400 mono-text">{selectedUserForPassword?.email}</p>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                placeholder="Enter new password (min 6 characters)"
                data-testid="new-password-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowPasswordDialog(false);
                  setNewPassword('');
                  setSelectedUserForPassword(null);
                }}
                variant="outline"
                className="flex-1 border-[#27272a] text-zinc-300"
              >
                CANCEL
              </Button>
              <Button
                onClick={changeUserPassword}
                className="flex-1 bg-[#facc15] text-black hover:bg-[#eab308] font-black"
                data-testid="change-password-submit"
              >
                CHANGE PASSWORD
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={showEditEventDialog} onOpenChange={setShowEditEventDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">EDIT EVENT</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300 uppercase-label">Event Name</Label>
              <Input
                value={editingEvent?.name || ''}
                onChange={(e) => setEditingEvent({ ...editingEvent, name: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="edit-event-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Description</Label>
              <Input
                value={editingEvent?.description || ''}
                onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="edit-event-description-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Start Date</Label>
              <Input
                type="date"
                value={editingEvent?.start_date || ''}
                onChange={(e) => setEditingEvent({ ...editingEvent, start_date: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowEditEventDialog(false);
                  setEditingEvent(null);
                }}
                variant="outline"
                className="flex-1 border-[#27272a] text-zinc-300"
              >
                CANCEL
              </Button>
              <Button
                onClick={updateEvent}
                disabled={loading}
                className="flex-1 bg-[#facc15] text-black hover:bg-[#eab308] font-black"
                data-testid="update-event-submit-button"
              >
                {loading ? 'UPDATING...' : 'UPDATE EVENT'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Stage Dialog */}
      <Dialog open={showEditStageDialog} onOpenChange={setShowEditStageDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">EDIT STAGE</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300 uppercase-label">Stage Name <span className="text-red-500">*</span></Label>
              <Input
                value={editingStage?.name || ''}
                onChange={(e) => setEditingStage({ ...editingStage, name: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                data-testid="edit-stage-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Stage Number {editingStage?.stage_type === 'stage' && <span className="text-red-500">*</span>}</Label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={editingStage?.stage_number || ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setEditingStage({ ...editingStage, stage_number: val === '' ? '' : parseInt(val) });
                }}
                className="mt-2 w-full bg-[#27272a] border border-[#27272a] text-white rounded-md px-3 py-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid="edit-stage-number-input"
                placeholder={editingStage?.stage_type !== 'stage' ? 'Optional for sections/service' : ''}
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Distance (km)</Label>
              <input
                type="text"
                inputMode="decimal"
                value={editingStage?.distance_km || ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, '');
                  const parts = val.split('.');
                  const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : val;
                  setEditingStage({ ...editingStage, distance_km: sanitized === '' ? '' : sanitized });
                }}
                className="mt-2 w-full bg-[#27272a] border border-[#27272a] text-white rounded-md px-3 py-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid="edit-stage-distance-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowEditStageDialog(false);
                  setEditingStage(null);
                }}
                variant="outline"
                className="flex-1 border-[#27272a] text-zinc-300"
              >
                CANCEL
              </Button>
              <Button
                onClick={updateStage}
                disabled={loading || !editingStage?.name || (editingStage?.stage_type === 'stage' && (editingStage?.stage_number === '' || editingStage?.stage_number === undefined))}
                className="flex-1 bg-[#facc15] text-black hover:bg-[#eab308] font-black disabled:opacity-50"
                data-testid="update-stage-submit-button"
              >
                {loading ? 'UPDATING...' : 'UPDATE STAGE'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Entry List Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">
              {editingEntry?.id ? 'EDIT ENTRY' : 'ADD ENTRY'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 uppercase-label text-xs">Car Number *</Label>
                <Input
                  value={editingEntry?.car_number || ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, car_number: e.target.value })}
                  className="mt-1 bg-[#27272a] border-[#27272a] text-white"
                  placeholder="001"
                  data-testid="entry-car-number-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300 uppercase-label text-xs">Class *</Label>
                <Input
                  value={editingEntry?.class_name || ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, class_name: e.target.value })}
                  className="mt-1 bg-[#27272a] border-[#27272a] text-white"
                  placeholder="Class A"
                  data-testid="entry-class-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label text-xs">Driver *</Label>
              <Input
                value={editingEntry?.driver || ''}
                onChange={(e) => setEditingEntry({ ...editingEntry, driver: e.target.value })}
                className="mt-1 bg-[#27272a] border-[#27272a] text-white"
                placeholder="Driver name"
                data-testid="entry-driver-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label text-xs">Co-Driver</Label>
              <Input
                value={editingEntry?.co_driver || ''}
                onChange={(e) => setEditingEntry({ ...editingEntry, co_driver: e.target.value })}
                className="mt-1 bg-[#27272a] border-[#27272a] text-white"
                placeholder="Co-driver name"
                data-testid="entry-codriver-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label text-xs">Entrant/Team</Label>
              <Input
                value={editingEntry?.entrant || ''}
                onChange={(e) => setEditingEntry({ ...editingEntry, entrant: e.target.value })}
                className="mt-1 bg-[#27272a] border-[#27272a] text-white"
                placeholder="Team name"
                data-testid="entry-entrant-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 uppercase-label text-xs">Drive Type</Label>
                <select
                  value={editingEntry?.drive_type || '2WD'}
                  onChange={(e) => setEditingEntry({ ...editingEntry, drive_type: e.target.value })}
                  className="mt-1 w-full bg-[#27272a] border-[#27272a] text-white rounded p-2"
                  data-testid="entry-drive-type-select"
                >
                  <option value="2WD">2WD</option>
                  <option value="4WD">4WD</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-300 uppercase-label text-xs">Clubmans</Label>
                <select
                  value={editingEntry?.clubmans ? 'yes' : 'no'}
                  onChange={(e) => setEditingEntry({ ...editingEntry, clubmans: e.target.value === 'yes' })}
                  className="mt-1 w-full bg-[#27272a] border-[#27272a] text-white rounded p-2"
                  data-testid="entry-clubmans-select"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label text-xs">Car/Model</Label>
              <Input
                value={editingEntry?.car_model || ''}
                onChange={(e) => setEditingEntry({ ...editingEntry, car_model: e.target.value })}
                className="mt-1 bg-[#27272a] border-[#27272a] text-white"
                placeholder="Ford Escort RS"
                data-testid="entry-car-model-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => {
                  setShowEntryDialog(false);
                  setEditingEntry(null);
                }}
                variant="outline"
                className="flex-1 border-[#27272a] text-zinc-300"
              >
                CANCEL
              </Button>
              <Button
                onClick={createOrUpdateEntry}
                disabled={loading || !editingEntry?.car_number || !editingEntry?.driver || !editingEntry?.class_name}
                className="flex-1 bg-[#facc15] text-black hover:bg-[#eab308] font-black disabled:opacity-50"
                data-testid="entry-submit-btn"
              >
                {loading ? 'SAVING...' : editingEntry?.id ? 'UPDATE' : 'ADD ENTRY'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Penalty Dialog */}
      <Dialog open={showPenaltyDialog} onOpenChange={setShowPenaltyDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#facc15]">ADD PENALTY</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300 uppercase-label">Car Number *</Label>
              <Input
                value={newPenalty.car_number}
                onChange={(e) => setNewPenalty({ ...newPenalty, car_number: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                placeholder="e.g., 001"
                data-testid="penalty-car-number-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Penalty Time (MM:SS) *</Label>
              <Input
                value={newPenalty.penalty_time}
                onChange={(e) => setNewPenalty({ ...newPenalty, penalty_time: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white font-mono"
                placeholder="e.g., 00:30"
                data-testid="penalty-time-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300 uppercase-label">Reason</Label>
              <Input
                value={newPenalty.reason}
                onChange={(e) => setNewPenalty({ ...newPenalty, reason: e.target.value })}
                className="mt-2 bg-[#27272a] border-[#27272a] text-white"
                placeholder="e.g., Jump start, speeding in service"
                data-testid="penalty-reason-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowPenaltyDialog(false);
                  setNewPenalty({ car_number: '', penalty_time: '', reason: '' });
                }}
                variant="outline"
                className="flex-1 border-[#27272a] text-zinc-300"
              >
                CANCEL
              </Button>
              <Button
                onClick={addPenalty}
                disabled={loading || !newPenalty.car_number || !newPenalty.penalty_time}
                className="flex-1 bg-orange-600 text-white hover:bg-orange-700 font-black disabled:opacity-50"
                data-testid="penalty-submit-btn"
              >
                {loading ? 'ADDING...' : 'ADD PENALTY'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;